import { useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from '../lib/auth.jsx';
import { isSchemaPending } from '../lib/supabase.js';
import DbPending from '../components/DbPending.jsx';
import * as db from '../lib/data.js';
import { toRailVM } from '../lib/viewmodel.js';
import { buildToday, buildTodayMock, mockReminders, reminderCalendar, mergeCalendars } from '../lib/today.js';
import { chime, chimeUrgent } from '../lib/chime.js';

// แท็บ "วันนี้" — คอกพิตงานประจำวัน: เร่งด่วนสุด → งานวันนี้ (ยืนยัน Form / แลก D/O /
// ตามเอกสาร / รับเข้าคลัง) → ตู้ค้างนาน; ขวา = งานของฉัน/เตือนความจำ + ปฏิทินของเข้า
// อ่านตู้อย่างเดียว (แก้ที่ Console); เขียนได้เฉพาะ reminder ของทีม

const TH_MONTHS = ['มกราคม','กุมภาพันธ์','มีนาคม','เมษายน','พฤษภาคม','มิถุนายน',
  'กรกฎาคม','สิงหาคม','กันยายน','ตุลาคม','พฤศจิกายน','ธันวาคม'];
const TH_DOW = ['อา','จ','อ','พ','พฤ','ศ','ส'];
const pad = (n) => String(n).padStart(2, '0');
const dayKey = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

const Ico = ({ d, w = 24, sw = 2 }) => (
  <svg viewBox="0 0 24 24" strokeWidth={sw} strokeLinecap="round" strokeLinejoin="round" fill="none"
    dangerouslySetInnerHTML={{ __html: d }}/>
);
const ClockIcon = () => <Ico d='<circle cx="12" cy="12" r="9"/><path d="M12 7v5l3 2"/>'/>;
const AlertIcon = () => <Ico d='<path d="M12 3 2 20h20L12 3z"/><path d="M12 10v4M12 17v.5"/>'/>;
const CheckIcon = () => <Ico sw={2.4} d='<path d="M4 12l5 5L20 6"/>'/>;
const PlusIcon = () => <Ico d='<path d="M12 5v14M5 12h14"/>'/>;
const TrashIcon = () => <Ico d='<path d="M4 7h16M9 7V5a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2M6 7l1 13a1 1 0 0 0 1 1h8a1 1 0 0 0 1-1l1-13"/>'/>;
const EditIcon = () => <Ico d='<path d="M12 20h9"/><path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4z"/>'/>;
const BellIcon = ({ off }) => <Ico d={off
  ? '<path d="M18 8a6 6 0 0 0-9.3-5"/><path d="M6 9v5l-2 3h13"/><path d="M10.5 20a2 2 0 0 0 3 0"/><path d="M3 3l18 18"/>'
  : '<path d="M18 8a6 6 0 1 0-12 0c0 6-2 6-2 9h16c0-3-2-3-2-9"/><path d="M10.5 20a2 2 0 0 0 3 0"/>'}/>;
const Arrow = () => <span className="t-arw">→</span>;

// ── งานหนึ่งแถว (ผูกตู้ + ปุ่มเปิดตู้) ──
function TaskRow({ item, onOpen }) {
  return (
    <div className="t-item">
      <span className="t-dot" aria-hidden="true"/>
      <div className="t-item-tx">
        <div className="t-item-t">{item.title}</div>
        <div className="t-item-s">{item.sub}</div>
      </div>
      <button className="t-go" onClick={() => onOpen(item.vm.id)}>เปิดตู้ <Arrow/></button>
    </div>
  );
}

function TaskGroup({ g, onOpen }) {
  const n = g.items.length;
  return (
    <div className="t-group">
      <div className="t-group-h">
        <span className="t-group-t">{g.title}</span>
        {n ? <span className="t-count">{n}</span>
           : <span className="t-done"><CheckIcon/> ไม่มีค้าง</span>}
      </div>
      <div className="t-hint">{g.hint}</div>
      {n > 0 && <div className="t-items">{g.items.map((it, i) =>
        <TaskRow key={(it.vm.id) + '_' + (it.consignmentId ?? i)} item={it} onOpen={onOpen}/>)}</div>}
    </div>
  );
}

// ── งานของฉัน / เตือนความจำ ──
// งาน "ของฉัน" = มอบหมายให้ฉัน หรือยังไม่มอบหมายและฉันเป็นคนสร้าง (deploy-24: reminder.assignee)
const isMineRem = (r, meId) => (r.assignee ? r.assignee === meId : (!r.created_by || r.created_by === meId));

const remSub = (r, shipName, partnerName = () => null, staffName = () => null) => {
  const bits = [];
  if (r.due_date) bits.push(fmtSel(r.due_date) + (r.due_time ? ` ${r.due_time}` : ''));
  else bits.push('ไม่มีกำหนด');
  if (r.assignee) bits.push(`→ ${staffName(r.assignee) || 'พนักงาน'}`);
  if (r.shipment_id) bits.push(`ตู้ ${shipName(r.shipment_id) || 'ถูกยกเลิก'}`);
  // ไม่มีสิทธิ์อ่านข้อมูลแบรนด์ (เช่น ทีมคลัง) → ข้ามบรรทัดนี้ไปเลย ไม่ใช่ขึ้นว่า "ถูกลบ"
  if (r.partner_id) { const nm = partnerName(r.partner_id); if (nm) bits.push(`แบรนด์ ${nm}`); }
  if (r.note) bits.push(r.note);
  return bits.join(' · ');
};

function ReminderForm({ initial, ships, partners = [], staff = [], meId = null, onSave, onCancel, busy }) {
  const [title, setTitle] = useState(initial?.title ?? '');
  const [date, setDate] = useState(initial?.due_date ?? '');
  const [time, setTime] = useState(initial?.due_time ?? '');
  const [ship, setShip] = useState(initial?.shipment_id ?? '');
  const [partner, setPartner] = useState(initial?.partner_id ?? '');
  const [assignee, setAssignee] = useState(initial ? (initial.assignee ?? '') : (meId ?? ''));   // งานใหม่ = ของฉันเป็นค่าเริ่มต้น
  const [note, setNote] = useState(initial?.note ?? '');
  const submit = (e) => {
    e.preventDefault();
    if (!title.trim()) return;
    onSave({ title, dueDate: date, dueTime: time, shipmentId: ship || null, partnerId: partner || null, assignee: assignee || null, note });
  };
  return (
    <form className="t-remform" onSubmit={submit}>
      <input className="t-in" autoFocus placeholder="งานที่ต้องทำ / เตือนความจำ…"
        value={title} onChange={e => setTitle(e.target.value)}/>
      <div className="t-remrow">
        <label className="t-remfield">
          <span>วันเตือน</span>
          <input className="t-in sm" type="date" value={date} onChange={e => setDate(e.target.value)}/>
        </label>
        <label className="t-remfield">
          <span>เวลา</span>
          <input className="t-in sm" type="time" value={time} onChange={e => setTime(e.target.value)}/>
        </label>
      </div>
      <div className="t-remrow">
        <label className="t-remfield">
          <span>ผูกกับตู้ (ถ้ามี)</span>
          <select className="t-in sm" value={ship} onChange={e => setShip(e.target.value)}>
            <option value="">— ไม่ผูก —</option>
            {ships.map(s => <option key={s.dbId} value={s.dbId}>{s.code} · {s.name}</option>)}
          </select>
        </label>
        {partners.length > 0 && (
          <label className="t-remfield">
            <span>ผูกกับแบรนด์ (ถ้ามี)</span>
            <select className="t-in sm" value={partner} onChange={e => setPartner(e.target.value)}>
              <option value="">— ไม่ผูก —</option>
              {partners.map(p => <option key={p.id} value={p.id}>{p.supplier_name}</option>)}
            </select>
          </label>
        )}
      </div>
      {staff.length > 0 && (
        <label className="t-remfield">
          <span>มอบหมายให้ใคร</span>
          <select className="t-in sm" value={assignee} onChange={e => setAssignee(e.target.value)}>
            <option value="">ทั้งทีม (ไม่ระบุคน)</option>
            {staff.map(s => <option key={s.user_id} value={s.user_id}>{s.name}{s.user_id === meId ? ' (ฉัน)' : ''}</option>)}
          </select>
        </label>
      )}
      <input className="t-in" placeholder="รายละเอียดเพิ่มเติม (ถ้ามี)"
        value={note} onChange={e => setNote(e.target.value)}/>
      <div className="t-rembtns">
        <button className="t-savebtn" disabled={busy || !title.trim()}>{initial ? 'บันทึกการแก้ไข' : 'เพิ่มงาน'}</button>
        <button type="button" className="t-cancelbtn" onClick={onCancel}>ยกเลิก</button>
      </div>
    </form>
  );
}

function MyTasks({ reminders, ships, partners, staff, meId, shipName, partnerName, staffName, onOpenShip, onOpenBrand, onAdd, onEdit, onToggle, onDelete, busy }) {
  const [open, setOpen] = useState(false);   // ฟอร์มเพิ่ม
  const [editId, setEditId] = useState(null);
  const [showDone, setShowDone] = useState(false);
  const [view, setView] = useState('mine');   // ของฉัน | ทั้งทีม — แต่ละคนเห็นลิสต์ของตัวเองก่อน
  const mineN = reminders.filter(r => !r.done && isMineRem(r, meId)).length;
  const allN = reminders.filter(r => !r.done).length;
  const shown = view === 'mine' ? reminders.filter(r => isMineRem(r, meId)) : reminders;
  const active = shown.filter(r => !r.done);
  const done = shown.filter(r => r.done);

  const save = async (fields) => {
    if (editId) await onEdit(editId, fields); else await onAdd(fields);
    setOpen(false); setEditId(null);
  };

  return (
    <div className="t-cal t-mytasks">
      <div className="t-cal-h">
        <span className="t-cal-title">งานของฉัน / เตือนความจำ</span>
        {!open && editId == null && (
          <button className="t-today-btn add" onClick={() => setOpen(true)}><PlusIcon/> เพิ่มงาน</button>
        )}
      </div>

      <div className="t-viewtabs">
        <button className={`t-viewtab ${view === 'mine' ? 'on' : ''}`} onClick={() => setView('mine')}>ของฉัน <b>{mineN}</b></button>
        <button className={`t-viewtab ${view === 'all' ? 'on' : ''}`} onClick={() => setView('all')}>ทั้งทีม <b>{allN}</b></button>
      </div>

      {open && editId == null && (
        <ReminderForm ships={ships} partners={partners} staff={staff} meId={meId} busy={busy} onSave={save} onCancel={() => setOpen(false)}/>
      )}

      {active.length === 0 && !open && (
        <div className="t-dd-empty">{view === 'mine' && allN > 0
          ? 'ยังไม่มีงานของคุณ — กด “เพิ่มงาน” หรือดู “ทั้งทีม”'
          : 'ยังไม่มีงานที่จดไว้ — กด “เพิ่มงาน” เพื่อบันทึกสิ่งที่ต้องทำ/เตือนตัวเอง'}</div>
      )}
      {active.map(r => editId === r.id ? (
        <ReminderForm key={r.id} initial={r} ships={ships} partners={partners} staff={staff} meId={meId} busy={busy}
          onSave={save} onCancel={() => setEditId(null)}/>
      ) : (() => {
        const linked = r.shipment_id && shipName(r.shipment_id);   // ตู้ยังอยู่ (ไม่ถูก void)
        const linkedBrand = !linked && r.partner_id && partnerName(r.partner_id);   // ไม่มีตู้แต่ผูกแบรนด์ → เปิดแบรนด์
        const go = () => { if (linked) onOpenShip(r.shipment_id); else if (linkedBrand) onOpenBrand(r.partner_id); };
        return (
        <div className="t-remitem" key={r.id}>
          <button className="t-check" title="ทำเสร็จแล้ว" onClick={() => onToggle(r, true)} aria-label="เสร็จ"/>
          <div className="t-item-tx" onClick={go}
            style={{ cursor: (linked || linkedBrand) ? 'pointer' : 'default' }}>
            <div className="t-item-t">{r.title}</div>
            <div className="t-item-s">{remSub(r, shipName, partnerName, staffName)}</div>
          </div>
          <div className="t-remacts">
            <button className="t-iconbtn" title="แก้ไข" onClick={() => { setEditId(r.id); setOpen(false); }}><EditIcon/></button>
            <button className="t-iconbtn del" title="ลบ" onClick={() => onDelete(r)}><TrashIcon/></button>
          </div>
        </div>
        );
      })())}

      {done.length > 0 && (
        <div className="t-donewrap">
          <button className="t-donetoggle" onClick={() => setShowDone(s => !s)}>
            {showDone ? '▾' : '▸'} เสร็จแล้ว ({done.length})
          </button>
          {showDone && done.map(r => (
            <div className="t-remitem done" key={r.id}>
              <button className="t-check on" title="ยกเลิกเสร็จ" onClick={() => onToggle(r, false)} aria-label="ยังไม่เสร็จ"><CheckIcon/></button>
              <div className="t-item-tx"><div className="t-item-t">{r.title}</div></div>
              <div className="t-remacts">
                <button className="t-iconbtn del" title="ลบ" onClick={() => onDelete(r)}><TrashIcon/></button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── ปฏิทินของเข้า (เดือนเดียว) + งานเตือนของฉัน ──
const KIND = {
  eta:  { mark: '●', label: 'ของถึง (ETA)' },
  etd:  { mark: '○', label: 'ออกจากต้นทาง (ETD)' },
  free: { mark: '▲', label: 'หมด free time' },
  reminder: { mark: '◆', label: 'เตือน' },
};

function Calendar({ calendar, unscheduled, onOpen, onOpenShip, shipName, partnerName = () => null, staffName = () => null, onOpenBrand = () => {} }) {
  const now = new Date();
  const [ym, setYm] = useState({ y: now.getFullYear(), m: now.getMonth() });
  const [sel, setSel] = useState(dayKey(now));

  const first = new Date(ym.y, ym.m, 1);
  const lead = first.getDay();
  const days = new Date(ym.y, ym.m + 1, 0).getDate();
  const todayKey = dayKey(now);

  const cells = [];
  for (let i = 0; i < lead; i++) cells.push(null);
  for (let d = 1; d <= days; d++) cells.push(`${ym.y}-${pad(ym.m + 1)}-${pad(d)}`);

  const step = (delta) => {
    const m = ym.m + delta, y = ym.y + Math.floor(m / 12);
    setYm({ y, m: ((m % 12) + 12) % 12 });
  };
  const selEvents = (calendar[sel] ?? []);

  return (
    <div className="t-cal">
      <div className="t-cal-h">
        <span className="t-cal-title">ปฏิทินของเข้า</span>
        <div className="t-cal-nav">
          <button className="t-navbtn" onClick={() => step(-1)} title="เดือนก่อน">‹</button>
          <span className="t-cal-month">{TH_MONTHS[ym.m]} {ym.y + 543}</span>
          <button className="t-navbtn" onClick={() => step(1)} title="เดือนถัดไป">›</button>
          <button className="t-today-btn" onClick={() => { setYm({ y: now.getFullYear(), m: now.getMonth() }); setSel(todayKey); }}>วันนี้</button>
        </div>
      </div>
      <div className="t-cal-legend">
        <span><i className="t-lg k-eta u-watch"/>ของถึง</span>
        <span><i className="t-lg k-etd u-ok"/>ออกต้นทาง</span>
        <span><i className="t-lg k-free u-urgent"/>หมด free time</span>
        <span><i className="t-lg k-reminder"/>เตือน</span>
      </div>
      <div className="t-grid">
        {TH_DOW.map(d => <div key={d} className="t-dow">{d}</div>)}
        {cells.map((key, i) => {
          if (!key) return <div key={'b' + i} className="t-cell empty"/>;
          const evs = calendar[key] ?? [];
          const dn = Number(key.slice(-2));
          return (
            <button key={key}
              className={`t-cell ${key === todayKey ? 'today' : ''} ${key === sel ? 'sel' : ''} ${evs.length ? 'has' : ''}`}
              onClick={() => setSel(key)}>
              <span className="t-daynum">{dn}</span>
              <span className="t-evs">
                {evs.slice(0, 4).map((ev, j) => <i key={j} className={`t-ev k-${ev.kind} u-${ev.urgency ?? 'na'}`}/>)}
                {evs.length > 4 && <span className="t-more">+{evs.length - 4}</span>}
              </span>
            </button>
          );
        })}
      </div>

      <div className="t-day-detail">
        <div className="t-dd-h">{fmtSel(sel)}</div>
        {selEvents.length ? selEvents.map((ev, i) => ev.kind === 'reminder' ? (
          <div className="t-dd-row" key={i}>
            <span className="t-dd-mark k-reminder">◆</span>
            <div className="t-dd-tx">
              <b>เตือน: {ev.reminder.title}</b>
              <span>{[ev.reminder.due_time,
                ev.reminder.assignee ? '→ ' + (staffName(ev.reminder.assignee) || 'พนักงาน') : null,
                ev.reminder.shipment_id ? 'ตู้ ' + (shipName(ev.reminder.shipment_id) || 'ถูกยกเลิก') : null,
                ev.reminder.partner_id ? (partnerName(ev.reminder.partner_id) ? 'แบรนด์ ' + partnerName(ev.reminder.partner_id) : null) : null,
                ev.reminder.note].filter(Boolean).join(' · ') || 'งานของฉัน'}</span>
            </div>
            {ev.reminder.shipment_id && shipName(ev.reminder.shipment_id)
              ? <button className="t-go sm" onClick={() => onOpenShip(ev.reminder.shipment_id)}>เปิดตู้ <Arrow/></button>
              : ev.reminder.partner_id && partnerName(ev.reminder.partner_id)
              ? <button className="t-go sm" onClick={() => onOpenBrand(ev.reminder.partner_id)}>เปิดแบรนด์ <Arrow/></button>
              : null}
          </div>
        ) : (
          <div className="t-dd-row" key={i}>
            <span className={`t-dd-mark k-${ev.kind} u-${ev.urgency ?? 'na'}`}>{KIND[ev.kind].mark}</span>
            <div className="t-dd-tx">
              <b>{KIND[ev.kind].label}</b>
              <span>{ev.code} · {ev.vm.displayName}</span>
            </div>
            <button className="t-go sm" onClick={() => onOpen(ev.vm.id)}>เปิดตู้ <Arrow/></button>
          </div>
        )) : <div className="t-dd-empty">ไม่มีของเข้า/กำหนดในวันนี้</div>}
      </div>

      {unscheduled.length > 0 && (
        <div className="t-unsched">
          <div className="t-unsched-h">ยังไม่กำหนด ETA ({unscheduled.length}) — ไม่ขึ้นปฏิทิน</div>
          {unscheduled.map(vm => (
            <div className="t-dd-row" key={vm.id}>
              <span className="t-dd-mark">·</span>
              <div className="t-dd-tx"><b>{vm.id}</b><span>{vm.displayName} · {vm.phase}</span></div>
              <button className="t-go sm" onClick={() => onOpen(vm.id)}>เปิดตู้ <Arrow/></button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function fmtSel(key) {
  const [y, m, d] = String(key).split('-').map(Number);
  return `${d} ${TH_MONTHS[m - 1]} ${y + 543}`;
}

const SOUND_KEY = 'ib_sound_on';

export default function Today({ onOpenShipment, onOpenBrand = () => {} }) {
  const { mock, session } = useAuth();
  const meId = mock ? 'u1' : (session?.user?.id ?? null);
  const [st, setSt] = useState({ loading: true });
  const [reminders, setReminders] = useState([]);
  const [ships, setShips] = useState([]);       // [{code,name}] สำหรับผูกงานกับตู้
  const [partners, setPartners] = useState([]); // [{id,supplier_name}] สำหรับผูกงานกับแบรนด์ (แท็บ "แบรนด์")
  const [staff, setStaff] = useState([]);       // [{user_id,name}] ทีมของแอพ สำหรับมอบหมายงาน
  const [busy, setBusy] = useState(false);
  const [nonce, setNonce] = useState(0);
  const [soundOn, setSoundOn] = useState(() => {
    try { return localStorage.getItem(SOUND_KEY) === '1'; } catch { return false; }
  });
  const alerted = useRef(false);   // เตือนครั้งเดียวต่อการโหลด ไม่รบกวนซ้ำ

  useEffect(() => {
    if (mock) {
      setSt({ loading: false, model: buildTodayMock(), demo: true });
      setReminders(mockReminders());
      // mock: dbId = code (ไม่มี FK) → routing/ผูกตู้ทำงานเหมือน live
      setShips([['SHP-2607-01','Snow Peak · เติมสต๊อก'],['SHP-2607-02','Nalgene'],
        ['SHP-2608-01','Klymit'],['SHP-2606-03','DOD Outdoors'],['SHP-2606-01','Barebones']]
        .map(([code, name]) => ({ code, dbId: code, name })));
      setPartners([{ id: 'p1', supplier_name: 'Snow Peak' }, { id: 'p2', supplier_name: 'DoD' }, { id: 'p3', supplier_name: 'NALGENE' }]);
      setStaff([{ user_id: 'u1', name: 'ปุ้ย' }, { user_id: 'u2', name: 'ปรินทร' }]);
      return;
    }
    let alive = true;
    alerted.current = false;   // โหลดใหม่ = เตือนได้อีกครั้ง
    setSt({ loading: true });
    (async () => {
      try {
        const { ships: rawShips, certs } = await db.loadTodayBoard();
        const vms = rawShips.map(toRailVM);
        const model = buildToday(vms, certs);
        const rem = await db.listReminders().catch(() => []);
        const parts = await db.listPartners().catch(() => []);   // ยังไม่รัน deploy-23 = ว่าง (ไม่พัง)
        const team = await db.listStaff().catch(() => []);        // deploy-24
        if (!alive) return;
        setSt({ loading: false, model });
        setReminders(rem);
        setPartners(parts.map(p => ({ id: p.id, supplier_name: p.supplier_name })));
        setStaff(team.filter(s => s.is_active !== false));
        setShips(vms.map(v => ({ code: v.id, dbId: v.dbId, name: v.displayName })));
      } catch (e) {
        if (!alive) return;
        if (isSchemaPending(e)) setSt({ loading: false, pending: true });
        else setSt({ loading: false, error: e.message ?? String(e) });
      }
    })();
    return () => { alive = false; };
  }, [mock, nonce]);

  const today = useMemo(() => {
    const d = new Date();
    return `${TH_DOW[d.getDay()]} ${d.getDate()} ${TH_MONTHS[d.getMonth()]} ${d.getFullYear() + 543}`;
  }, [nonce]);

  // reminder.shipment_id เก็บเป็น UUID (dbId) → map เป็นชื่อ + code สำหรับเปิดตู้
  const shipName = useMemo(() => {
    const m = Object.fromEntries(ships.map(s => [s.dbId, s.name]));
    return (dbId) => m[dbId] || null;   // null = ตู้หาย/ถูกยกเลิก (อย่าโชว์ UUID ดิบ)
  }, [ships]);
  const openByShip = useMemo(() => {
    const m = Object.fromEntries(ships.map(s => [s.dbId, s.code]));
    return (dbId) => { const code = m[dbId]; if (code) onOpenShipment(code); };
  }, [ships, onOpenShipment]);
  // reminder.partner_id → ชื่อแบรนด์ (null = แบรนด์ถูกลบ ไม่โชว์ UUID ดิบ)
  const partnerName = useMemo(() => {
    const m = Object.fromEntries(partners.map(p => [p.id, p.supplier_name]));
    return (id) => m[id] || null;
  }, [partners]);
  const staffName = useMemo(() => {
    const m = Object.fromEntries(staff.map(s => [s.user_id, s.name]));
    return (id) => m[id] || null;
  }, [staff]);

  // ── reminder actions (mock = แก้ใน state; live = เขียน DB แล้วรีเฟรช) ──
  const refreshReminders = async () => { setReminders(await db.listReminders().catch(() => reminders)); };
  const toRow = (f) => ({ title: f.title, note: f.note, due_date: f.dueDate, due_time: f.dueTime, shipment_id: f.shipmentId || null, partner_id: f.partnerId || null, assignee: f.assignee || null, created_by: meId });
  const onAdd = async (f) => {
    if (st.demo) { setReminders(r => [{ id: 'tmp' + Date.now(), done: false, ...toRow(f) }, ...r]); return; }
    setBusy(true); try { await db.addReminder(f); await refreshReminders(); } finally { setBusy(false); }
  };
  const onEdit = async (id, f) => {
    if (st.demo) { setReminders(r => r.map(x => x.id === id ? { ...x, ...toRow(f) } : x)); return; }
    setBusy(true); try { await db.updateReminder(id, f); await refreshReminders(); } finally { setBusy(false); }
  };
  const onToggle = async (r, done) => {
    if (st.demo) { setReminders(rs => rs.map(x => x.id === r.id ? { ...x, done } : x)); return; }
    setBusy(true); try { await db.setReminderDone(r.id, done); await refreshReminders(); } finally { setBusy(false); }
  };
  const onDelete = async (r) => {
    if (st.demo) { setReminders(rs => rs.filter(x => x.id !== r.id)); return; }
    setBusy(true); try { await db.deleteReminder(r.id); await refreshReminders(); } finally { setBusy(false); }
  };

  const calendarMerged = useMemo(() => {
    if (!st.model) return {};
    return mergeCalendars(st.model.calendar, reminderCalendar(reminders));
  }, [st.model, reminders]);

  // ── เสียงเตือน "เรื่องที่ต้องทำวันนี้" ──
  const todayK = dayKey(new Date());
  // เสียงเตือน = เฉพาะงาน "ของฉัน" ที่ถึงกำหนดวันนี้ (แต่ละคนได้ยินของตัวเอง)
  const dueTodayN = reminders.filter(r => !r.done && isMineRem(r, meId) && String(r.due_date || '').slice(0, 10) === todayK).length;
  const toggleSound = () => setSoundOn(v => {
    const nv = !v;
    try { localStorage.setItem(SOUND_KEY, nv ? '1' : '0'); } catch { /* โหมดส่วนตัว */ }
    if (nv) chime();          // เสียงยืนยัน + ปลดล็อกเสียงของเบราว์เซอร์ (ต้องเกิดจากการกดเท่านั้น)
    return nv;
  });
  useEffect(() => {
    if (!soundOn || alerted.current || st.loading || !st.model) return;
    const urgentN = st.model.urgent.length;
    if (!urgentN && !dueTodayN) return;
    alerted.current = true;
    if (urgentN) chimeUrgent(); else chime();
  }, [soundOn, st.loading, st.model, dueTodayN]);

  if (st.loading) return <div className="placeholder"><h2>กำลังโหลดงานวันนี้…</h2></div>;
  if (st.pending) return <DbPending onRetry={() => setNonce(n => n + 1)}/>;
  if (st.error) return (
    <div className="placeholder">
      <h2>โหลดงานวันนี้ไม่สำเร็จ</h2><p className="mono">{st.error}</p>
      <button className="tab" onClick={() => setNonce(n => n + 1)}>ลองใหม่</button>
    </div>
  );

  const { urgent, tasks, backlog, unscheduled, counts } = st.model;
  const open = onOpenShipment;
  const noWork = !counts.active;
  const activeRem = reminders.filter(r => !r.done && isMineRem(r, meId)).length;

  const rightCol = (
    <div className="t-col t-col-r">
      <MyTasks reminders={reminders} ships={ships} partners={partners} staff={staff} meId={meId}
        shipName={shipName} partnerName={partnerName} staffName={staffName}
        onOpenShip={openByShip} onOpenBrand={onOpenBrand}
        onAdd={onAdd} onEdit={onEdit} onToggle={onToggle} onDelete={onDelete} busy={busy}/>
      <Calendar calendar={calendarMerged} unscheduled={unscheduled} onOpen={open} onOpenShip={openByShip} shipName={shipName}
        partnerName={partnerName} staffName={staffName} onOpenBrand={onOpenBrand}/>
    </div>
  );

  return (
    <div className="today">
      <div className="t-head">
        <div className="t-head-l">
          <h1 className="t-title">วันนี้</h1>
          <span className="t-date">{today}</span>
        </div>
        <div className="t-chips">
          {st.demo && <span className="schip warm">โหมดตัวอย่าง</span>}
          <span className="schip">กำลังทำ <b>{counts.active}</b></span>
          {counts.urgent > 0 && <span className="schip hot">ต้องเร่ง <b>{counts.urgent}</b></span>}
          <span className="schip">งานวันนี้ <b>{counts.tasks}</b></span>
          {activeRem > 0 && <span className="schip">งานของฉัน <b>{activeRem}</b></span>}
          {counts.backlog > 0 && <span className="schip warm">ค้างนาน <b>{counts.backlog}</b></span>}
          <button className={`t-sound ${soundOn ? 'on' : ''}`} onClick={toggleSound}
            title={soundOn ? 'เสียงเตือนเปิดอยู่ — กดเพื่อปิด' : 'เปิดเสียงเตือนเรื่องด่วน/งานที่ถึงกำหนดวันนี้'}>
            <BellIcon off={!soundOn}/>
          </button>
          <button className="t-refresh" onClick={() => setNonce(n => n + 1)} title="โหลดใหม่">↻</button>
        </div>
      </div>

      {noWork ? (
        <div className="t-cols">
          <div className="t-col">
            <div className="placeholder" style={{ marginTop: 4 }}>
              <h2>ยังไม่มีตู้ที่กำลังทำ</h2>
              <p>ไปที่แท็บ Console เพื่อสร้างตู้ใหม่ — งานประจำวันจะโผล่ที่นี่เอง<br/>ระหว่างนี้จดงาน/เตือนความจำของคุณไว้ทางขวาได้เลย</p>
            </div>
          </div>
          {rightCol}
        </div>
      ) : (
        <div className="t-cols">
          <div className="t-col">
            {/* 1 · เร่งด่วนสุดวันนี้ */}
            {urgent.length ? (
              <div className={`t-hero t-hero-live u-${urgent[0].vm.urgency === 'urgent' ? 'urgent' : 'watch'}`}>
                <div className="t-hero-bar"/>
                <div className="t-hero-bd">
                  <div className="t-hero-top"><AlertIcon/><span>เร่งด่วนที่สุดตอนนี้</span></div>
                  <div className="t-hero-id">{urgent[0].vm.id} · {urgent[0].vm.displayName}</div>
                  <div className="t-hero-why">{urgent[0].reason}</div>
                  <button className="t-hero-go" onClick={() => open(urgent[0].vm.id)}>จัดการเดี๋ยวนี้ <Arrow/></button>
                </div>
                {urgent.length > 1 && (
                  <div className="t-hero-more">
                    {urgent.slice(1, 4).map(u => (
                      <div className="t-item" key={u.vm.id}>
                        <span className={`t-dot u-${u.vm.urgency}`}/>
                        <div className="t-item-tx">
                          <div className="t-item-t">{u.vm.id} · {u.vm.displayName}</div>
                          <div className="t-item-s">{u.reason}</div>
                        </div>
                        <button className="t-go" onClick={() => open(u.vm.id)}>เปิดตู้ <Arrow/></button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <div className="t-hero calm">
                <div className="t-hero-bar"/>
                <div className="t-hero-bd">
                  <div className="t-hero-top ok"><CheckIcon/><span>ไม่มีเรื่องเร่งด่วนวันนี้</span></div>
                  <div className="t-hero-why">ทุกตู้ยังมีเวลา — ทำงานตามลิสต์ด้านล่างได้ตามสบาย</div>
                </div>
              </div>
            )}

            {/* 2 · งานวันนี้ (4 เลน) — จอกว้างเรียง 2 คอลัมน์ */}
            <div className="t-section-t">งานวันนี้</div>
            <div className="t-lanes">
              {tasks.map(g => <TaskGroup key={g.key} g={g} onOpen={open}/>)}
            </div>

            {/* 3 · ตู้ค้างนาน */}
            <div className="t-section-t">ตู้ค้างนาน / ตู้เก่า</div>
            <div className="t-group">
              {backlog.length ? (
                <div className="t-items">
                  {backlog.map(b => (
                    <div className="t-item" key={b.vm.id}>
                      <span className="t-dot warn"/>
                      <div className="t-item-tx">
                        <div className="t-item-t">{b.vm.id} · {b.vm.displayName}</div>
                        <div className="t-item-s">{b.reason}</div>
                      </div>
                      <button className="t-go" onClick={() => open(b.vm.id)}>เปิดตู้ <Arrow/></button>
                    </div>
                  ))}
                </div>
              ) : <div className="t-empty-inline"><CheckIcon/> ไม่มีตู้ค้างนาน</div>}
            </div>
          </div>

          {rightCol}
        </div>
      )}
    </div>
  );
}
